import React, { FC, useRef, useCallback } from 'react';

import { useInView } from 'react-intersection-observer';
import Lottie, { LottieOptions, LottieRef } from 'lottie-react';

import decorate from '../decorate/decorate';
import Box from '../box/box';

export interface VisibleLottieProps extends Omit<LottieOptions, 'animationData'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The lottie animation JSON data. */
  data?: string;
  /** Alt description of the animation */
  alt?: string;
  /** Set the initial value of the inView boolean. */
  initialInView?: boolean;
  /** The path the lottie animation JSON file. */
  path?: string;
  /** The threshold for when the component is in the viewport. */
  threshold?: number;
}

const VisibleLottieComponent: FC<VisibleLottieProps> = (
  {
    className,
    data,
    alt,
    initialInView = true,
    loop = false,
    path,
    renderer = 'svg',
    rendererSettings,
    threshold = 0.5
  }
) => {
  const lottieRef: LottieRef = useRef(null);
  const [inViewRef] = useInView({ threshold, initialInView });
  const setRefs = useCallback(
    node => {
      lottieRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  const animationData = data
    ? JSON.parse(data)
    : path;

  const animationOptions = {
    animationData: animationData,
    loop,
    renderer,
    rendererSettings: {
      ...rendererSettings,
      filterSize: {
        width: '200%',
        height: '200%',
        x: '-50%',
        y: '-50%'
      }
    }
  };

  return (
    <Box
      ref={setRefs}
      className={className}
    >
      <Lottie
        {...animationOptions}
        alt={alt || ''}
        lottieRef={lottieRef}
      />
    </Box>
  );
};

const VisibleLottie = decorate<VisibleLottieProps>(VisibleLottieComponent);

export { VisibleLottie };
export default VisibleLottie;
